import _ from 'lodash';
import { useSirenEntity } from '~/utility/BinSentry-ui-utility';
import { useMemo } from 'react';

/**
 * Return physical bins owned by the barn
 * @param barnId
 * @returns {{bins: Entity[], loading: boolean}}
 */
export function useBarnBins({ barnId }) {
  const { entity: barnEntity, loading: barnLoading } = useSirenEntity({ href: barnId });
  const { entity: binsEntity, loading: binsLoading } = useSirenEntity({ href: barnEntity?.getLink('https://api.binsentry.com/rel/owned-bins').href });
  // sorting by name is a sane default, and also ensures that whoever asks for the bins gets them in the same order,
  // so that when we assign colours from a palette for bins in the barn, they get the same colour everywhere.
  // Using localeCompare with numeric = true means that "Bin 9" will come before "Bin 10" as opposed to the reverse in
  // a naive alphabetical sort
  const bins = useMemo(() => {
    try {
      return _.chain(binsEntity?.getSubEntities('item') || [])
        .sort((a, b) => a.properties.name.localeCompare(b.properties.name, undefined, { numeric: true, sensitivity: 'base' })) // sort takes in comparator
        .value();
    } catch (e) {
      // Fallback to lodash if localeCompare is not available
      return _.sortBy(binsEntity?.getSubEntities('item'), 'properties.name');
    }
  }, [binsEntity]);
  return { bins, loading: barnLoading || binsLoading };
}

/**
 * Return organization (virtual) bin that represents the barn
 * @param barnId
 * @returns {{barnBin: Entity, loading: boolean}}
 */
export function useBarnOrganizationBin({ barnId }) {
  const { entity: barn, loading: barnLoading } = useSirenEntity({ href: barnId });
  // The barn organization bin is created downstream. Potential race condition, where organization-bin is not present on barnEntity
  const barnBinId = barn?.getLink('https://api.binsentry.com/rel/organization-bin')?.href;
  const { entity: barnBin, loading: barnBinLoading } = useSirenEntity({ href: barnBinId });

  return { barnEntity: barn, barnBin, loading: barnLoading || barnBinLoading };
}
